import {FC} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'

type Props = {
  visible: boolean
  recordType: string
  vesselName?: string
  findingsCount?: number
  findingAttachmentsCount?: number
  loading?: boolean
  onClose: () => void
  onConfirm: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1055,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const MODAL_CONTENT_STYLE: React.CSSProperties = {
  maxWidth: '480px',
  width: '90%',
  borderRadius: '12px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
}

export const DeleteRecordConfirmModal: FC<Props> = ({
  visible,
  recordType,
  vesselName,
  findingsCount = 0,
  findingAttachmentsCount = 0,
  loading = false,
  onClose,
  onConfirm,
}) => {
  if (!visible) return null

  const hasFindings = findingsCount > 0
  const hasFindingAttachments = findingAttachmentsCount > 0
  const isBlocked = hasFindings || hasFindingAttachments

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={WRAP_STYLE}
      onClick={onClose}
    >
      <div
        className='modal-content bg-white p-6'
        style={MODAL_CONTENT_STYLE}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex justify-content-end'>
          <button
            type='button'
            className='btn btn-icon btn-sm btn-active-light-primary'
            onClick={onClose}
            disabled={loading}
          >
            <span className='fs-4 fw-bold'>&times;</span>
          </button>
        </div>

        <div className='text-center py-2'>
          <div className='d-flex justify-content-center mb-4'>
            {isBlocked ? (
              <div
                className='d-flex align-items-center justify-content-center rounded-circle bg-light-warning'
                style={{width: '72px', height: '72px'}}
              >
                <KTSVG
                  path='/media/map/exclamation.svg'
                  className='svg-icon-3x text-warning'
                />
              </div>
            ) : (
              <div
                className='d-flex align-items-center justify-content-center rounded-circle bg-light-danger'
                style={{width: '72px', height: '72px'}}
              >
                <KTSVG
                  path='/media/map/trash.svg'
                  className='svg-icon-3x text-danger'
                />
              </div>
            )}
          </div>

          <h4 className='fw-bolder text-dark mb-3'>
            {isBlocked
              ? `Cannot Delete ${recordType}`
              : `Delete ${recordType}`}
          </h4>

          {hasFindings ? (
            <div className='text-muted fs-6 mb-6 px-4'>
              This {recordType.toLowerCase()} cannot be deleted because it contains{' '}
              <strong className='text-dark'>{findingsCount}</strong> associated finding
              {findingsCount > 1 ? 's' : ''}.
              <div className='mt-2 text-danger fs-7'>
                Please delete all associated findings before deleting this {recordType.toLowerCase()}.
              </div>
            </div>
          ) : hasFindingAttachments ? (
            <div className='text-muted fs-6 mb-6 px-4'>
              This {recordType.toLowerCase()} cannot be deleted because it contains{' '}
              <strong className='text-dark'>{findingAttachmentsCount}</strong> finding
              attachment{findingAttachmentsCount > 1 ? 's' : ''}.
              <div className='mt-2 text-danger fs-7'>
                Please delete or remove the finding attachments before deleting this {recordType.toLowerCase()}.
              </div>
            </div>
          ) : (
            <div className='text-muted fs-6 mb-6 px-4'>
              Are you sure you want to delete this {recordType.toLowerCase()}{vesselName ? ` for vessel "${vesselName}"` : ''}?
              <div className='mt-1 text-danger fs-7'>
                This action cannot be undone.
              </div>
            </div>
          )}

          <div className='d-flex justify-content-center gap-3 mt-4'>
            {isBlocked ? (
              <button
                type='button'
                className='btn btn-primary px-6'
                onClick={onClose}
              >
                OK, Got It
              </button>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-light px-6'
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-danger px-6 d-flex align-items-center'
                  onClick={onConfirm}
                  disabled={loading}
                >
                  {loading && (
                    <span className='spinner-border spinner-border-sm me-2' role='status' />
                  )}
                  Yes, Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteRecordConfirmModal
